import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { quoteApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { getTranslateByKey } from "@/main/services/utils";

import { TypeCreateOrderBodyshopStep } from "./types";
import {
	buildQuoteFlagsFromQuote,
	buildVehicleDataFromQuote,
	getQuoteBlockedReasonKey,
	INITIAL_QUOTE_FLAGS,
	IQuoteFlags,
} from "./utilsQuoteDetail";
import {
	getStepByQuoteDetail,
	getStepsBefore,
	isStepNavigationLockedByStatus,
	STEPS_CREATE_ORDER_BODYSHOP,
} from "./utilsSteps";
import { IDamagePart } from "./steps/damageParts/types";
import { mapQuoteItemsToDamageParts } from "./steps/damageParts/utilsDamageParts";
import { fetchQuoteItemConditions } from "./steps/summary/services/summaryService";
import { IPartOffer } from "./steps/summary/types";
import { mapQuoteItemsToOffers } from "./steps/summary/utilsSummary";
import {
	IAiVehicleResult,
	IVehicleManualForm,
	IVehicleSelected,
	TypeVehiclePhotos,
	VehicleModeEnum,
} from "./steps/vehicleData/types";

const INITIAL_FORM_STATE = {
	vehicleData: {
		mode: null as VehicleModeEnum | null,
		photos: {} as TypeVehiclePhotos,
		isDrivable: true,
		aiResult: null as IAiVehicleResult | null,
		guidCountry: null as string | null,
		plate: "",
		vin: "",
		manualForm: null as IVehicleManualForm | null,
		vehicle: null as IVehicleSelected | null,
	},
	damageArea: {
		selectedAreas: [] as KeyValue[],
	},
	damageParts: {
		selectedParts: [] as IDamagePart[],
	},
	summary: {
		offers: [] as IPartOffer[],
	},
};
export type TypeDataFormCreateOrderBodyshop = typeof INITIAL_FORM_STATE;

export interface IStepHead {
	title?: string;
	subtitle?: string;
	isHidden?: boolean;
	idProcurementStatus?: number | null;
}

interface CreateOrderBodyshopStepsContextData {
	currentStep: TypeCreateOrderBodyshopStep;
	completedSteps: TypeCreateOrderBodyshopStep[];
	formData: TypeDataFormCreateOrderBodyshop;
	guidQuote: string | null;
	isEdit: boolean;
	isDirtyForm: boolean;
	isLoadingQuote: boolean;
	hasErrorQuote: boolean;
	isQuoteReadOnly: boolean;
	isStepNavigationLocked: boolean;
	quoteBlockedReasonKey: string;
	quoteFlags: IQuoteFlags;
	clientSecret: string;
	stepHead: IStepHead;
	canGoBack: boolean;
	retryLoadQuote: () => void;
	refreshQuoteFlags: () => void;
	setClientSecret: (clientSecret: string) => void;
	setIsPaymentPending: (isPaymentPending: boolean) => void;
	setStepHead: (head: IStepHead) => void;
	setStepBack: (onBack: (() => void) | null) => void;
	setIsDirtyForm: (isDirty: boolean) => void;
	updateGuidQuote: (guidQuote: string) => void;
	updateFormData: (data: Partial<TypeDataFormCreateOrderBodyshop>) => void;
	navigateToStep: (step: TypeCreateOrderBodyshopStep) => void;
	nextStep: () => void;
	previousStep: () => void;
	goBack: () => void;
}

export const CreateOrderBodyshopStepsContext = createContext<CreateOrderBodyshopStepsContextData>(
	{} as CreateOrderBodyshopStepsContextData,
);

export const useCreateOrderBodyshopSteps = () => useContext(CreateOrderBodyshopStepsContext);

export function useStepHead({ title, subtitle, isHidden, idProcurementStatus }: IStepHead) {
	const { setStepHead } = useCreateOrderBodyshopSteps();

	useEffect(() => {
		setStepHead({ title, subtitle, isHidden, idProcurementStatus });
		return () => setStepHead({});
	}, [title, subtitle, isHidden, idProcurementStatus]);
}

export function useStepBack(onBack: () => void) {
	const { setStepBack } = useCreateOrderBodyshopSteps();
	const onBackRef = useRef(onBack);
	onBackRef.current = onBack;

	useEffect(() => {
		setStepBack(() => onBackRef.current());
		return () => setStepBack(null);
	}, []);
}

interface ICreateOrderBodyshopStepsProviderProps {
	children: React.ReactNode;
}

export const CreateOrderBodyshopStepsProvider = ({ children }: ICreateOrderBodyshopStepsProviderProps) => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const guidQuoteFromUrl = useRef(searchParams.get("guidQuote"));

	const [currentStep, setCurrentStep] = useState<TypeCreateOrderBodyshopStep>(STEPS_CREATE_ORDER_BODYSHOP[0]);
	const [completedSteps, setCompletedSteps] = useState<TypeCreateOrderBodyshopStep[]>([]);
	const [formData, setFormData] = useState<TypeDataFormCreateOrderBodyshop>(INITIAL_FORM_STATE);
	const [guidQuote, setGuidQuote] = useState<string | null>(guidQuoteFromUrl.current);
	const [isDirtyForm, setIsDirtyFormState] = useState<boolean>(false);
	const [isLoadingQuote, setIsLoadingQuote] = useState<boolean>(Boolean(guidQuoteFromUrl.current));
	const [hasErrorQuote, setHasErrorQuote] = useState<boolean>(false);
	const [quoteBlockedReasonKey, setQuoteBlockedReasonKey] = useState<string>("");
	const [quoteFlags, setQuoteFlags] = useState<IQuoteFlags>(INITIAL_QUOTE_FLAGS);
	const [clientSecret, setClientSecret] = useState<string>("");
	const [stepHead, setStepHead] = useState<IStepHead>({});
	const [hasCustomStepBack, setHasCustomStepBack] = useState<boolean>(false);

	const stepBackRef = useRef<(() => void) | null>(null);

	const isEdit = completedSteps.includes(currentStep);
	const isStepNavigationLocked = isStepNavigationLockedByStatus(quoteFlags.idProcurementStatus);
	const canGoBack =
		!isStepNavigationLocked && (hasCustomStepBack || STEPS_CREATE_ORDER_BODYSHOP.indexOf(currentStep) > 0);

	function completeStep(step: TypeCreateOrderBodyshopStep) {
		if (completedSteps.includes(step)) return;
		setCompletedSteps([...new Set([...completedSteps, ...getStepsBefore(step), step])]);
	}

	async function loadQuoteDetail(guidQuoteProp: string) {
		try {
			setIsLoadingQuote(true);
			setHasErrorQuote(false);
			const { data } = await quoteApi.getQuoteById(guidQuoteProp);
			const { result } = data;

			if (result.hasOrder) {
				navigate(`${RoutesConstants.orderSearchOrder}?quoteFilterVehicle=${result.orderNumber}`);
				return;
			}

			setQuoteBlockedReasonKey(getQuoteBlockedReasonKey(result));
			setQuoteFlags(buildQuoteFlagsFromQuote(result));

			const stepFromQuote = getStepByQuoteDetail(result);
			const conditions = stepFromQuote === "Summary" ? await fetchQuoteItemConditions(guidQuoteProp) : null;

			setFormData((previous) => ({
				...previous,
				vehicleData: buildVehicleDataFromQuote(result),
				damageParts: {
					selectedParts: conditions ? mapQuoteItemsToDamageParts(conditions) : [],
				},
				summary: {
					offers: conditions ? mapQuoteItemsToOffers(conditions) : [],
				},
			}));
			setCompletedSteps(getStepsBefore(stepFromQuote));
			setCurrentStep(stepFromQuote);
		} catch {
			toast.error(getTranslateByKey("information.somethingWrong"));
			setHasErrorQuote(true);
		} finally {
			setIsLoadingQuote(false);
		}
	}

	function setIsDirtyForm(value: boolean) {
		setIsDirtyFormState(value);
	}

	function retryLoadQuote() {
		if (guidQuote) loadQuoteDetail(guidQuote);
	}

	async function refreshQuoteFlags() {
		if (!guidQuote) return;
		try {
			const { data } = await quoteApi.getQuoteById(guidQuote);
			const { result } = data;
			setQuoteBlockedReasonKey(getQuoteBlockedReasonKey(result));
			setQuoteFlags(buildQuoteFlagsFromQuote(result));
		} catch {}
	}

	function setIsPaymentPending(value: boolean) {
		setQuoteFlags((previous) => ({ ...previous, isPaymentPending: value }));
	}

	function updateGuidQuote(value: string) {
		setGuidQuote(value);
		setSearchParams(
			(previous) => {
				const updated = new URLSearchParams(previous);
				updated.set("guidQuote", value);
				return updated;
			},
			{ replace: true },
		);
	}

	function nextStep() {
		const indexCurrent = STEPS_CREATE_ORDER_BODYSHOP.indexOf(currentStep);
		const next = STEPS_CREATE_ORDER_BODYSHOP[indexCurrent + 1];
		if (!next) return;
		completeStep(currentStep);
		setCurrentStep(next);
	}

	function previousStep() {
		if (isStepNavigationLocked) return;
		const indexCurrent = STEPS_CREATE_ORDER_BODYSHOP.indexOf(currentStep);
		const previous = STEPS_CREATE_ORDER_BODYSHOP[indexCurrent - 1];
		if (!previous) return;
		setCurrentStep(previous);
	}

	function setStepBack(onBack: (() => void) | null) {
		stepBackRef.current = onBack;
		setHasCustomStepBack(Boolean(onBack));
	}

	function goBack() {
		if (!canGoBack) return;
		if (stepBackRef.current) {
			stepBackRef.current();
			return;
		}
		previousStep();
	}

	function navigateToStep(step: TypeCreateOrderBodyshopStep) {
		if (isStepNavigationLocked) return;
		const indexStep = STEPS_CREATE_ORDER_BODYSHOP.indexOf(step);
		if (indexStep < 0) return;
		if (indexStep > 0) {
			completeStep(STEPS_CREATE_ORDER_BODYSHOP[indexStep - 1]);
		}
		setCurrentStep(step);
	}

	function updateFormData(data: Partial<TypeDataFormCreateOrderBodyshop>) {
		setFormData((previous) => ({ ...previous, ...data }));
	}

	useEffect(() => {
		if (guidQuoteFromUrl.current) loadQuoteDetail(guidQuoteFromUrl.current);
	}, []);

	useEffect(() => {
		setIsDirtyForm(false);
	}, [currentStep]);

	useEffect(() => {
		if (!isDirtyForm) return undefined;

		function handleBeforeUnload(event: BeforeUnloadEvent) {
			event.preventDefault();
			event.returnValue = "";
		}

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [isDirtyForm]);

	return (
		<CreateOrderBodyshopStepsContext.Provider
			value={{
				currentStep,
				completedSteps,
				formData,
				guidQuote,
				isEdit,
				isDirtyForm,
				isLoadingQuote,
				hasErrorQuote,
				isQuoteReadOnly: Boolean(quoteBlockedReasonKey),
				isStepNavigationLocked,
				quoteBlockedReasonKey,
				quoteFlags,
				clientSecret,
				stepHead,
				canGoBack,
				retryLoadQuote,
				refreshQuoteFlags,
				setClientSecret,
				setIsPaymentPending,
				setStepHead,
				setStepBack,
				setIsDirtyForm,
				updateGuidQuote,
				updateFormData,
				navigateToStep,
				nextStep,
				previousStep,
				goBack,
			}}
		>
			{children}
		</CreateOrderBodyshopStepsContext.Provider>
	);
};

export default CreateOrderBodyshopStepsProvider;
